import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getSingleReservation,
  selectSingleReservation,
  DownloadSingleReservation,
} from '../redux/feature/reservation/slice';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Table, Spin, Tooltip, message } from 'antd';
import { formatedPrice, formatTime } from '../utils/helper';
import { Icon } from '@iconify/react';
import { motion } from "framer-motion";
import GoogleMap from "../components/GoogleMap";
import { debounce } from 'lodash';

function Main() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { tripId } = useParams();
  const trip = useSelector(selectSingleReservation);
  const [loader, setLoader] = useState(true);
  const remainingAmount = trip?.total - trip?.paid;
  const progressPercentage = Math.round((trip?.paid / trip?.total) * 100) || 0;

  useEffect(() => {
    if (tripId) {
      dispatch(getSingleReservation({ id: tripId, isTrip: true }));
    }
    setLoader(false);
  }, [dispatch, tripId]);

  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadClick = debounce(async () => {
    setIsDownloading(true);
    try {
      const response = await dispatch(DownloadSingleReservation({ id: tripId, isTrip: true }));
      if (response?.payload) {
        const pdfBlob = new Blob([response.payload], { type: 'application/pdf' });
        const pdfUrl = URL.createObjectURL(pdfBlob);
        window.open(pdfUrl, '_blank');
        setTimeout(() => URL.revokeObjectURL(pdfUrl), 10000);
        message.success('PDF opened in new tab');
      } else {
        message.error('No PDF data received');
      }
    } catch (error) {
      console.error("Error during download dispatch:", error);
      message.error('Failed to download PDF');
    } finally {
      setIsDownloading(false);
    }
  }, 500);

  const handlePayNow = () => {
    navigate(`/links/trip-checkout/${tripId}`);
  };



  const columns = [
    {
      title: 'Vehicle Name',
      dataIndex: 'name',
      key: 'name',
      align: 'center',
    },
    {
      title: "Vehicle Type",
      dataIndex: ["type", "name"],
      key: "type.name",
      align: 'center',
    },
    {
      title: "General",
      key: 'general',
      align: 'center',
      render: (text, record) => {
        if (!record?.features?.general) return '—';
        const availableFeatures = Object.entries(record.features.general)
          .filter(([_, feature]) => feature.isAvailable)
          .map(([_, feature]) => ({ name: feature.name, icon: feature.icon }));
        return (
          <Tooltip
            title={
              availableFeatures.length > 0 ? (
                <div>
                  {availableFeatures.map((feature, index) => (
                    <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
                      <Icon icon={feature.icon} style={{ color: 'white', marginRight: '8px' }} />
                      <span>{feature.name}</span>
                    </div>
                  ))}
                </div>
              ) : 'No available features'
            }
          >
            <span style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer' }}>
              {availableFeatures.length > 0 ? (
                <Icon icon="fluent:cursor-hover-28-regular" style={{ fontSize: '24px' }} />
              ) : (
                <Icon icon="ph:empty" style={{ fontSize: '24px' }} />
              )}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: "Multimedia",
      key: 'multimedia',
      align: 'center',
      render: (text, record) => {
        if (!record?.features?.multimedia) return '—';
        const availableFeatures = Object.entries(record.features.multimedia)
          .filter(([_, feature]) => feature.isAvailable)
          .map(([_, feature]) => ({ name: feature.name, icon: feature.icon }));
        return (
          <Tooltip
            title={
              availableFeatures.length > 0 ? (
                <div>
                  {availableFeatures.map((feature, index) => (
                    <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
                      <Icon icon={feature.icon} style={{ color: 'white', marginRight: '8px' }} />
                      <span>{feature.name}</span>
                    </div>
                  ))}
                </div>
              ) : 'No available features'
            }
          >
            <span style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer' }}>
              {availableFeatures.length > 0 ? (
                <Icon icon="fluent:cursor-hover-28-regular" style={{ fontSize: '24px' }} />
              ) : (
                <Icon icon="ph:empty" style={{ fontSize: '24px' }} />
              )}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: "Policies",
      key: 'policies',
      align: 'center',
      render: (text, record) => {
        if (!record?.features?.policies) return '—';
        const availablePolicies = Object.entries(record.features.policies)
          .filter(([_, policy]) => policy.isAvailable)
          .map(([_, policy]) => ({ name: policy.name, icon: policy.icon }));
        return (
          <Tooltip
            title={
              availablePolicies.length > 0 ? (
                <div>
                  {availablePolicies.map((policy, index) => (
                    <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
                      <Icon icon={policy.icon} style={{ color: 'white', marginRight: '8px' }} />
                      <span>{policy.name}</span>
                    </div>
                  ))}
                </div>
              ) : 'No available policies'
            }
          >
            <span style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer' }}>
              {availablePolicies.length > 0 ? (
                <Icon icon="fluent:cursor-hover-28-regular" style={{ fontSize: '24px' }} />
              ) : (
                <Icon icon="ph:empty" style={{ fontSize: '24px' }} />
              )}
            </span>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <div className="checkout-page bg-gray-100 min-h-screen p-6" style={{ zoom: '0.8' }}>
      {loader || !trip?._id ? (
        <div className="flex justify-center items-center h-screen">
          <Spin size="large" />
        </div>
      ) : (
        <div className="checkout-content md:max-w-4xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex flex-col md:flex-row justify-between gap-4">
            {/* Left Column — Company branding */}
            <div className="flex flex-col items-start gap-1">
              {trip?.tripAccociateCompanyId?.logo && (
                <div className="w-[6rem] h-[3rem] rounded-md overflow-hidden">
                  <img
                    src={trip.tripAccociateCompanyId.logo}
                    alt={`${trip.tripAccociateCompanyId?.name || 'Company'} logo`}
                    className="w-full h-full object-contain"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                </div>
              )}
              <span className="text-xl font-semibold">
                {trip?.tripAccociateCompanyId?.name}
              </span>
            </div>

            {/* Right Column — Price + Actions */}
            <div className="flex flex-col items-end space-y-2">
              <span className="text-xl font-semibold">
                {remainingAmount > 0 ? `Remaining - ${formatedPrice(remainingAmount)}` : 'Fully Paid'}
              </span>
              <div className="flex flex-wrap items-center gap-2">
                {/* Pay Now Button */}
                {remainingAmount > 0 && (
                  <button
                    className="bg-[#058CBD] rounded-md text-lg font-semibold px-5 py-2 text-white flex items-center space-x-2 hover:bg-[#047aad] transition-colors duration-300 shadow-md"
                    onClick={handlePayNow}
                  >
                    <Icon icon="mdi:credit-card-outline" className="text-2xl mr-2" />
                    Pay Now
                  </button>
                )}
                {/* Download Button */}
                <button
                  className="border-2 border-[#058CBD] rounded-md text-lg font-semibold px-4 py-2 text-[#058CBD] flex items-center space-x-2 hover:bg-[#058CBD] hover:text-white transition-colors duration-300"
                  onClick={() => handleDownloadClick()}
                  disabled={isDownloading}
                >
                  {isDownloading ? (
                    <Icon icon="line-md:loading-loop" className="text-2xl mr-2" />
                  ) : (
                    <Icon icon="line-md:download-loop" className="text-2xl mr-2" />
                  )}
                  {isDownloading ? 'Opening PDF...' : 'Download Summary'}
                </button>

              </div>
            </div>
          </div>

          {/* Trip Info Card */}
          <Card className="shadow-lg" title={`Trip ID: ${trip?.tripId || 'Loading...'}`}>
            <div className="flex wrap flex-col md:flex-row items-start h-full md:items-start md:gap-6 justify-center w-full p-4 text-xs md:text-sm">
              <div className="flex wrap flex-col items-start gap-3 w-full h-full mb-4 md:mb-0">
                <div className="w-full flex wrap flex-row items-center justify-between text-[#121417] md:self-start">
                  {trip?.tripType && (
                    <div className="bg-[#ECE7E1] p-2 px-3 rounded-md font-medium text-center">
                      {trip.tripType.charAt(0).toUpperCase() + trip.tripType.slice(1)}
                    </div>
                  )}
                  {trip?.orderType && (
                    <div className="bg-[#ECE7E1] p-2 px-3 rounded-md font-medium text-center">
                      {trip.orderType.charAt(0).toUpperCase() + trip.orderType.slice(1)}
                    </div>
                  )}
                </div>
                <div className="w-full flex wrap items-center">
                  <Icon height={25} icon="bx:car" className="mr-3" />
                  <div>
                    <p>
                      {trip?.pickupAddress?.address}
                      <br />
                      <span className="text-[#61758A]">
                        {trip?.pickupDate} | {formatTime(trip?.pickupTime)}
                      </span>
                    </p>
                  </div>
                </div>
                <motion.div
                  className="w-1 ml-3"
                  style={{
                    background: "linear-gradient(to bottom, #058cbd 50%, transparent 50%)",
                    backgroundSize: "3px 10px",
                    backgroundRepeat: "repeat-y",
                  }}
                  initial={{ height: 0 }}
                  animate={{ height: "5vh" }}
                  transition={{ duration: 1, ease: "easeOut" }}
                />
                <div className="w-full flex wrap items-center">
                  <Icon height={25} icon="bx:car" className="mr-3" />
                  <div>
                    <p>{trip?.dropoffAddress?.address}</p>
                  </div>
                </div>
              </div>

              <div className="flex wrap flex-col w-full md:w-2/3 h-full items-end justify-between">
                <div className="w-full mt-4 md:mt-0">
                  <div className="relative flex wrap border-2 overflow-hidden rounded-lg">
                    <GoogleMap
                      addressA={trip?.pickupAddress?.address}
                      addressB={trip?.dropoffAddress?.address}
                    />
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* Payment Summary Card */}
          {trip?.total > 0 && (
            <Card className="shadow-lg" title="Payment Summary">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total Amount</span>
                  <span className="font-semibold text-green-600">{formatedPrice(trip?.total)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Paid</span>
                  <span className="font-semibold text-[#058CBD]">{formatedPrice(trip?.paid || 0)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Remaining</span>
                  <span className="font-semibold text-red-600">{formatedPrice(remainingAmount)}</span>
                </div>
                <div className="pt-2">
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div
                      className="h-2.5 rounded-full transition-all duration-500"
                      style={{
                        width: `${Math.min(progressPercentage, 100)}%`,
                        background: 'linear-gradient(90deg, #058CBD, #87d068)',
                      }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1 text-right">{progressPercentage}% paid</p>
                </div>
                {remainingAmount > 0 && (
                  <button
                    className="w-full bg-[#058CBD] text-white font-semibold py-3 rounded-lg hover:bg-[#047aad] transition-colors duration-300 flex items-center justify-center gap-2 mt-2 shadow-md"
                    onClick={handlePayNow}
                  >
                    <Icon icon="mdi:credit-card-outline" className="text-xl" />
                    Pay {formatedPrice(remainingAmount)} Now
                  </button>
                )}
              </div>
            </Card>
          )}

          {/* Passenger & Client Details */}
          <div className="flex wrap flex-col h-full md:items-start md:gap-6 justify-center w-full md:text-sm shadow-lg">
            <Card
              title={
                <div className="flex wrap align-center items-center p-2 mx-0">
                  <p>Trip Participants</p>
                  <Icon icon="material-symbols:group" className="ms-auto w-8 h-8 text-[#058CBD]" />
                </div>
              }
              bordered={false}
              className="w-full"
            >
              {(() => {
                const passengerName = `${trip?.passengerFirstName || ""} ${trip?.passengerLastName || ""}`.trim();
                const customerName = `${trip?.customer?.firstName || ""} ${trip?.customer?.lastName || ""}`.trim();
                // If isPassenger is false, it means the client is NOT the passenger
                const isDifferent = trip?.isPassenger === false;

                console.log("DEBUG: isPassenger flag:", trip?.isPassenger);
                console.log("DEBUG: isDifferent result:", isDifferent);

                if (isDifferent) {
                  return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      {/* BILL TO */}
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 border-b pb-1">
                          <Icon icon="mdi:invoice-text-outline" className="text-[#058CBD] w-5 h-5" />
                          <span className="font-bold text-gray-700 uppercase tracking-wider text-xs">Bill To</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                            <Icon icon="material-symbols:person" className="w-6 h-6 text-gray-500" />
                          </div>
                          <div>
                            <p className="font-semibold text-base">{customerName}</p>
                            <p className="text-gray-500 text-xs">{trip?.customer?.email}</p>
                            <p className="text-gray-500 text-xs">{trip?.customer?.phoneNumber || trip?.customer?.phone}</p>
                          </div>
                        </div>
                      </div>

                      {/* PASSENGER */}
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 border-b pb-1">
                          <Icon icon="mdi:account-tie" className="text-[#058CBD] w-5 h-5" />
                          <span className="font-bold text-gray-700 uppercase tracking-wider text-xs">Passenger</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-[#058CBD]/10 flex items-center justify-center">
                            <Icon icon="material-symbols:person-play" className="w-6 h-6 text-[#058CBD]" />
                          </div>
                          <div>
                            <p className="font-semibold text-base">{passengerName}</p>
                            <p className="text-gray-500 text-xs">{trip?.passengerEmail}</p>
                            <p className="text-gray-500 text-xs">{trip?.passengerPhone}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }

                return (
                  <div className="flex wrap flex-col md:flex-row items-center gap-6">
                    <div className="w-16 h-16 rounded-full overflow-hidden shadow-md border-2 border-[#058CBD]/20">
                      <img
                        className="w-full h-full object-cover"
                        src={trip?.customer?.picture || "https://www.svgrepo.com/show/61986/avatar.svg"}
                        alt="Passenger"
                      />
                    </div>
                    <div className="flex-1 space-y-1">
                      <p className="text-xl font-bold text-gray-800">{customerName}</p>
                      <div className="flex flex-wrap gap-x-6 gap-y-1 text-gray-600">
                        <div className="flex items-center gap-2">
                          <Icon icon="clarity:email-solid" className="w-4 h-4 text-[#058CBD]" />
                          <span>{trip?.customer?.email}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Icon icon="ph:phone-fill" className="w-4 h-4 text-[#058CBD]" />
                          <span>{trip?.customer?.phone || trip?.customer?.phoneNumber}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              <div className="flex wrap flex-col md:flex-row gap-4 mt-8 border-t pt-6">
                {[
                  { icon: "mdi:users", label: "Passengers", value: trip?.passengers || trip?.passenger },
                  { icon: "material-symbols-light:luggage", label: "Luggage", value: trip?.luggage || trip?.lauggage },
                  { icon: "mingcute:seat-fill", label: "Car Seats", value: trip?.carSeat },
                ].map((item, index) => (
                  <Card key={index} className="flex-1 border border-gray-100 bg-gray-50/50 hover:bg-gray-50 transition-colors shadow-sm">
                    <div className="flex wrap flex-col items-center gap-1">
                      <Icon icon={item.icon} className="text-gray-400 w-5 h-5" />
                      <p className="text-gray-500 text-xs font-medium">{item.label}</p>
                      <p className="text-2xl font-bold text-[#058CBD]">{item.value}</p>
                    </div>
                  </Card>
                ))}
              </div>
            </Card>
          </div>

          {/* Vehicle Table */}
          {trip?.Vehicle && (
            <Card className="shadow-lg" title="Vehicle">
              <div className="overflow-x-auto">
                <Table
                  columns={columns}
                  dataSource={[trip.Vehicle]}
                  rowKey="_id"
                  pagination={false}
                  className="min-w-full"
                />
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

export default Main;
